import React, { ButtonHTMLAttributes, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import {
  classes,
  IconButton,
  useCombinedRefs,
  useSlug,
} from '@bloomberg/media-ui-core';
import { Exit } from '@bloomberg/media-ui-icons';
import { default as FocusTrap } from 'focus-trap-react';
import { GlobalProps, GlobalPropsWithChildren } from 'src/types';

import Portal from '../Portal';

import styles from './Drawer.module.css';

export type DrawerProps = GlobalProps & {
  children?: React.ReactNode;
  open: boolean;
  onClose: () => void;
};

export interface CompoundedComponent
  extends React.ForwardRefExoticComponent<
    DrawerProps & React.RefAttributes<HTMLDivElement>
  > {
  Header: React.FC<GlobalPropsWithChildren>;
  Content: React.FC<GlobalPropsWithChildren>;
  Button: React.FC<GlobalPropsWithChildren & ButtonHTMLAttributes<HTMLElement>>;
}

export const Drawer = React.forwardRef<HTMLDivElement, DrawerProps>(
  function DrawerComponent(props: DrawerProps, ref) {
    const { children, open = false, name = 'drawer', onClose } = props;

    const slugName = useSlug(name);
    const cx = classes([styles.base], props);
    const transitionRef = useRef<HTMLDivElement>(null);
    const combinedRef = useCombinedRefs(transitionRef, ref);

    return (
      <Portal>
        <CSSTransition
          in={open}
          timeout={300}
          mountOnEnter
          nodeRef={transitionRef}
          classNames={{
            enter: styles.enter,
            enterActive: styles['enter-active'],
            exit: styles.exit,
            exitActive: styles['exit-active'],
          }}
          unmountOnExit
        >
          <div className={cx} ref={combinedRef} data-component={slugName}>
            <div className="overlay" />
            <FocusTrap
              active={open}
              focusTrapOptions={{
                fallbackFocus: '.main',
                clickOutsideDeactivates: onClose,
                escapeDeactivates: onClose,
              }}
            >
              <div
                className="main"
                role="dialog"
                aria-labelledby="dialog_label"
                aria-modal="true"
              >
                {children}
                <IconButton
                  className="close-button"
                  onClick={onClose}
                  title="Close menu"
                  size="small"
                >
                  <Exit />
                </IconButton>
              </div>
            </FocusTrap>
          </div>
        </CSSTransition>
      </Portal>
    );
  }
) as CompoundedComponent;

function DrawerHeaderComponent({
  children,
  name = 'drawer-header',
  ...props
}: GlobalPropsWithChildren) {
  return (
    <div
      className={classes([styles.header], props)}
      data-component={useSlug(name)}
      id="dialog-label"
    >
      {children}
    </div>
  );
}

Drawer.Header = DrawerHeaderComponent;
Drawer.Header.displayName = 'Drawer.Header';

function DrawerContentComponent({
  children,
  name = 'drawer-content',
  ...props
}: GlobalPropsWithChildren) {
  return (
    <div
      className={classes([styles.content], props)}
      data-component={useSlug(name)}
    >
      {children}
    </div>
  );
}

Drawer.Content = DrawerContentComponent;
Drawer.Content.displayName = 'Drawer.Content';

function DrawerButtonComponent({
  children,
  name = 'drawer-button',
  className,
  ...props
}: GlobalPropsWithChildren) {
  return (
    <button
      className={classes([styles.button], { className, ...props })}
      data-component={useSlug(name)}
      {...props}
    >
      {children}
    </button>
  );
}

Drawer.Button = DrawerButtonComponent;
Drawer.Button.displayName = 'Drawer.Button';
